<template>
  <SatellitesPage>
    <template #actions>
      <button class="btn _bg-green sat-btn-inline" :disabled="loading || limitReached" @click="openAdd">
        <SatIcon name="plus" />
        {{ $t("satellites.domains.add_button") }}
      </button>
    </template>

    <div v-if="loading" class="d-flex justify-content-center py-5">
      <img :src="require('@/assets/images/rolling.svg')" alt="loader" class="loader" />
    </div>

    <template v-else>
      <div v-if="!domains.length" class="sat-empty">
        <span class="sat-empty__title">{{ $t("satellites.domains.empty_title") }}</span>
        <span class="sat-empty__text">{{ $t("satellites.domains.empty_description") }}</span>
        <button class="btn _bg-green sat-btn-inline" @click="openAdd"><SatIcon name="plus" /> {{ $t("satellites.domains.add_button") }}</button>
        <DomainHelp class="domains-empty-help" open />
      </div>

      <template v-else>
        <div class="domains-toolbar">
          <div class="domains-search">
            <SatIcon name="search" :size="15" />
            <input v-model.trim="search" type="search" :placeholder="$t('satellites.domains.search')" />
          </div>
          <div class="domains-filters" role="tablist">
            <button v-for="filter in filters" :key="filter.key" type="button" class="domains-filter" :class="{ _active: activeFilter === filter.key }" @click="setFilter(filter.key)">
              {{ filter.label }}
              <span class="domains-filter__count">{{ filter.count }}</span>
            </button>
          </div>
          <span class="domains-counter" :class="{ _full: limitReached }">{{ $t("satellites.domains.counter", { count, limit }) }}</span>
        </div>

        <div v-if="filterChip" class="domains-active-filter">
          <span class="sat-chip _accent">
            {{ filterChip }}
            <button type="button" class="domains-active-filter__clear" :title="$t('satellites.domains.clear_filter')" @click="clearScope"><SatIcon name="x" :size="12" /></button>
          </span>
        </div>

        <div v-if="selected.length" class="sat-bulk">
          <span class="sat-bulk__count">{{ pl("satellites.counts.selected", selected.length) }}</span>
          <SatPopover :width="300">
            <template #trigger>
              <button class="btn _border _green sat-btn-inline" :disabled="bulkSaving"><SatIcon name="route" /> {{ $t("satellites.domains.assign_router") }} <SatIcon name="chevron" :size="14" /></button>
            </template>
            <template #default="{ close }">
              <RouterMenu :routers="routers" :value="commonRouterId" @select="(id) => (close(), bulkAssign({ router_id: id }))" />
            </template>
          </SatPopover>
          <button class="btn _border _green sat-btn-inline" :disabled="bulkSaving" @click="openPlacement(selected)"><SatIcon name="server" /> {{ $t("satellites.domains.set_placement") }}</button>
          <button class="btn _border sat-btn-inline domains-danger-btn" :disabled="bulkSaving" @click="askDelete(selected)"><SatIcon name="trash" /> {{ $t("satellites.delete") }}</button>
          <span class="sat-bulk__spacer"></span>
          <button type="button" class="sat-icon-btn" :title="$t('satellites.domains.clear_selection')" @click="clearSelection"><SatIcon name="x" /></button>
        </div>

        <div class="c-table sat-table mt-3">
          <div class="inner">
            <vue-good-table
              ref="table"
              styleClass="table _no-wrap"
              :columns="columns"
              :rows="visibleDomains"
              :select-options="{ enabled: true, selectOnCheckboxOnly: true, disableSelectInfo: true }"
              @on-selected-rows-change="onSelectionChange"
            >
              <template slot="table-column" slot-scope="props">
                <span v-if="props.column.label">{{ $t(props.column.label) }}</span>
              </template>

              <template slot="table-row" slot-scope="props">
                <template v-if="props.column.field === 'host'">
                  <div class="domains-host">
                    <a :href="`https://${props.row.host}`" target="_blank" rel="noopener noreferrer" class="domains-host__name">{{ props.row.host }}</a>
                    <span class="domains-host__ssl" :class="{ _ok: !!props.row.certificate }">
                      <SatIcon name="lock" :size="11" />
                      {{ props.row.certificate ? $t("satellites.domains.ssl_until", { date: formatDay(props.row.certificate.not_after) }) : $t("satellites.domains.ssl_on_demand") }}
                    </span>
                  </div>
                </template>

                <template v-else-if="props.column.field === 'router'">
                  <SatPopover :width="300">
                    <template #trigger>
                      <button type="button" class="domains-cell-btn" :class="{ _empty: !props.row.router }" :disabled="savingIds.includes(props.row.id)">
                        <template v-if="props.row.router">{{ props.row.router.name }}</template>
                        <template v-else><SatIcon name="alert" :size="13" /> {{ $t("satellites.domains.no_router") }}</template>
                        <SatIcon name="chevron" :size="13" class="domains-cell-btn__chevron" />
                      </button>
                    </template>
                    <template #default="{ close }">
                      <RouterMenu :routers="routers" :value="props.row.router ? props.row.router.id : null" @select="(id) => (close(), updateOne(props.row, { router_id: id }))" />
                    </template>
                  </SatPopover>
                </template>

                <template v-else-if="props.column.field === 'placement'">
                  <button type="button" class="domains-cell-btn _plain" :disabled="savingIds.includes(props.row.id)" @click="openPlacement([props.row])">
                    <PlacementBadge :placement="props.row.placement" />
                    <span class="domains-sats" :class="{ _none: !props.row.satellites_count }">{{ pl("satellites.counts.satellites", props.row.satellites_count) }}</span>
                  </button>
                </template>

                <template v-else-if="props.column.field === 'control'">
                  <div class="domains-controls">
                    <router-link :to="{ name: 'SatelliteLinks', query: { domain: props.row.id } }" class="sat-icon-btn" :title="$t('satellites.links.open')">
                      <SatIcon name="link" />
                    </router-link>
                    <button type="button" class="sat-icon-btn _danger" :title="$t('satellites.delete')" @click="askDelete([props.row])"><SatIcon name="trash" /></button>
                  </div>
                </template>
              </template>
              <div class="d-flex justify-content-center sat-muted" slot="emptystate">{{ $t("satellites.domains.nothing_found") }}</div>
            </vue-good-table>
          </div>
        </div>

        <DomainHelp class="mt-4" />
      </template>
    </template>

    <!-- Add domains -->
    <modal name="satelliteDomainsAdd" :width="640" height="auto" :scrollable="true" classes="creative-modal" @closed="resetAdd">
      <div class="domains-modal">
        <svg v-svg @click="$modal.hide('satelliteDomainsAdd')" class="close" symbol="modal-close" size="0 0 15 15" role="info"></svg>
        <h3 class="modal-title pt-2">{{ $t("satellites.domains.add_title") }}</h3>

        <div class="sat-field mt-4">
          <label class="sat-field__label" for="domains-hosts">{{ $t("satellites.domains.hosts_label") }}</label>
          <textarea id="domains-hosts" ref="hosts" v-model="hostsText" rows="5" class="domains-textarea" :placeholder="$t('satellites.domains.placeholder')" :class="{ invalid: !!clientError || !!addError || hostErrors.length > 0 }"></textarea>
          <div class="sat-field__hint">
            {{ $t("satellites.domains.add_hint", { number: maxHosts }) }}
            <b v-if="entries.length">{{ pl("satellites.counts.domains", entries.length) }}</b>
          </div>
          <div v-if="clientError" class="sat-field__error">{{ clientError }}</div>
          <ul v-if="hostErrors.length" class="domains-host-errors">
            <li v-for="item in hostErrors" :key="item.index">
              <b>{{ item.host }}</b> — {{ item.message }}
            </li>
          </ul>
        </div>

        <div class="sat-field">
          <span class="sat-field__label">{{ $t("satellites.domains.router_label") }}</span>
          <div class="domains-router-options">
            <button type="button" class="domains-router-option" :class="{ _active: addRouterId === null }" @click="addRouterId = null">{{ $t("satellites.domains.router_later") }}</button>
            <button v-for="router in routers" :key="router.id" type="button" class="domains-router-option" :class="{ _active: addRouterId === router.id }" @click="addRouterId = router.id">{{ router.name }}</button>
          </div>
          <div v-if="addViolation('router_id')" class="sat-field__error">{{ addViolation("router_id") }}</div>
        </div>

        <div class="sat-field">
          <span class="sat-field__label">{{ $t("satellites.domains.placement_label") }}</span>
          <PlacementPicker v-model="addPlacement" :groups="groups" :satellites="satellites" />
          <div v-if="placementError" class="sat-field__error">{{ placementError }}</div>
        </div>

        <div v-if="addError" class="sat-note _danger mt-3"><SatIcon name="alert" />{{ addError }}</div>

        <div class="domains-modal__actions">
          <button class="btn _bg-gray" @click="$modal.hide('satelliteDomainsAdd')">{{ $t("cancel") }}</button>
          <button class="btn _bg-green sat-btn-inline" :disabled="adding || !entries.length || !!clientError" @click="add">
            <img v-if="adding" src="@/assets/images/rolling-button.svg" class="loader-button" alt="" />
            {{ entries.length ? pl("satellites.domains.add_n", entries.length) : $t("satellites.domains.add") }}
          </button>
        </div>
      </div>
    </modal>

    <!-- Placement of one or several domains -->
    <modal name="satelliteDomainsPlacement" :width="600" height="auto" :scrollable="true" classes="creative-modal" @closed="placementTargets = []">
      <div class="domains-modal">
        <svg v-svg @click="$modal.hide('satelliteDomainsPlacement')" class="close" symbol="modal-close" size="0 0 15 15" role="info"></svg>
        <h3 class="modal-title pt-2">{{ $t("satellites.domains.placement_title") }}</h3>
        <p class="domains-modal__subtitle">{{ placementTargets.length === 1 ? placementTargets[0].host : pl("satellites.counts.domains", placementTargets.length) }}</p>
        <PlacementPicker v-model="placementDraft" :groups="groups" :satellites="satellites" class="mt-3" />
        <div v-if="placementError" class="sat-field__error">{{ placementError }}</div>
        <div class="domains-modal__actions">
          <button class="btn _bg-gray" @click="$modal.hide('satelliteDomainsPlacement')">{{ $t("cancel") }}</button>
          <button class="btn _bg-green sat-btn-inline" :disabled="bulkSaving" @click="savePlacement">
            <img v-if="bulkSaving" src="@/assets/images/rolling-button.svg" class="loader-button" alt="" />
            {{ $t("save") }}
          </button>
        </div>
      </div>
    </modal>

    <!-- Delete -->
    <modal name="satelliteDomainsDelete" :width="520" height="auto" :scrollable="true" classes="creative-modal" @closed="deleteTargets = []">
      <div class="domains-modal text-center">
        <svg v-svg @click="$modal.hide('satelliteDomainsDelete')" class="close" symbol="modal-close" size="0 0 15 15" role="info"></svg>
        <h3 class="modal-title pt-2">
          {{ deleteTargets.length === 1 ? $t("satellites.domains.delete_confirm", { host: deleteTargets[0].host }) : $t("satellites.domains.delete_confirm_many", { count: pl("satellites.counts.domains", deleteTargets.length) }) }}
        </h3>
        <p class="domains-modal__subtitle mt-2">{{ $t("satellites.domains.delete_note") }}</p>
        <div class="domains-modal__actions justify-content-center">
          <button class="btn _bg-gray" @click="$modal.hide('satelliteDomainsDelete')">{{ $t("cancel") }}</button>
          <button class="btn _bg-green" :disabled="bulkSaving" @click="remove">{{ $t("satellites.delete") }}</button>
        </div>
      </div>
    </modal>
  </SatellitesPage>
</template>

<script>
import { isEqual } from "lodash";
import satelliteService from "@/api/satelliteService";
import SatellitesPage from "@/components/Satellites/SatellitesPage.vue";
import SatIcon from "@/components/Satellites/SatIcon.vue";
import SatPopover from "@/components/Satellites/SatPopover.vue";
import RouterMenu from "@/components/Satellites/RouterMenu.vue";
import PlacementBadge from "@/components/Satellites/PlacementBadge.vue";
import PlacementPicker from "@/components/Satellites/PlacementPicker.vue";
import DomainHelp from "@/components/Satellites/DomainHelp.vue";
import satelliteFeatureGuard from "@/components/Satellites/satelliteFeatureGuard";
import { formatMixin, isOk, messagesOf } from "@/components/Satellites/format";

// Hosts per request, the backend accepts 1..100
const MAX_HOSTS = 100;

// Domains typed in the textarea: one per line, a line may also hold several separated by spaces or commas.
// Returns [{ host, line }] without empty and repeated (case insensitive) hosts; line is 1-based.
const parseHosts = (text) => {
  const seen = new Set();
  const entries = [];
  (text || "").split(/\r?\n/).forEach((content, index) => {
    content
      .split(/[\s,;]+/)
      .map((host) => host.trim())
      .filter((host) => host.length > 0)
      .forEach((host) => {
        const key = host.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        entries.push({ host, line: index + 1 });
      });
  });
  return entries;
};

// Placement of a domain as the API takes it
const placementBody = (placement) =>
  placement.mode === "all" ? { mode: "all" } : { mode: "selected", group_ids: placement.group_ids, satellite_ids: placement.satellite_ids };

const placementDraftOf = (placement) => ({
  mode: placement.mode,
  group_ids: placement.groups.map((group) => group.id),
  satellite_ids: placement.satellites.map((satellite) => satellite.id),
});

export default {
  name: "SatelliteDomains",
  components: { SatellitesPage, SatIcon, SatPopover, RouterMenu, PlacementBadge, PlacementPicker, DomainHelp },
  mixins: [formatMixin],
  beforeRouteEnter: satelliteFeatureGuard,
  data() {
    return {
      loading: true,
      domains: [],
      count: 0,
      limit: 0,
      routers: [],
      groups: [],
      satellites: [],
      search: "",
      activeFilter: "all",
      selected: [],
      savingIds: [],
      bulkSaving: false,
      // Add
      maxHosts: MAX_HOSTS,
      hostsText: "",
      addRouterId: null,
      addPlacement: { mode: "all", group_ids: [], satellite_ids: [] },
      adding: false,
      sentEntries: [],
      violations: {},
      addError: "",
      // Placement and delete modals
      placementTargets: [],
      placementDraft: { mode: "all", group_ids: [], satellite_ids: [] },
      deleteTargets: [],
      columns: [
        { label: "satellites.domains.columns.host", field: "host", sortable: false },
        { label: "satellites.domains.columns.router", field: "router", sortable: false },
        { label: "satellites.domains.columns.placement", field: "placement", sortable: false },
        { label: "", field: "control", sortable: false, width: "90px" },
      ],
    };
  },
  computed: {
    limitReached() {
      return this.limit > 0 && this.count >= this.limit;
    },
    // Scope from the query: ?router= or ?group= (links from the router and group pages)
    scopeRouter() {
      return this.routers.find((router) => router.id === this.$route.query.router) || null;
    },
    scopeGroup() {
      return this.groups.find((group) => group.id === this.$route.query.group) || null;
    },
    filterChip() {
      if (this.scopeRouter) return this.$t("satellites.domains.scope_router", { name: this.scopeRouter.name });
      if (this.scopeGroup) return this.$t("satellites.domains.scope_group", { name: this.scopeGroup.name });
      return "";
    },
    scopedDomains() {
      let domains = this.domains;
      if (this.scopeRouter) domains = domains.filter((d) => d.router && d.router.id === this.scopeRouter.id);
      if (this.scopeGroup) domains = domains.filter((d) => d.placement.mode === "all" || d.placement.groups.some((g) => g.id === this.scopeGroup.id));
      const search = this.search.toLowerCase();
      return search ? domains.filter((d) => d.host.includes(search)) : domains;
    },
    filters() {
      const count = (predicate) => this.scopedDomains.filter(predicate).length;
      return [
        { key: "all", label: this.$t("satellites.domains.filters.all"), count: this.scopedDomains.length, test: () => true },
        { key: "no_router", label: this.$t("satellites.domains.filters.no_router"), count: count((d) => !d.router), test: (d) => !d.router },
        { key: "no_satellites", label: this.$t("satellites.domains.filters.no_satellites"), count: count((d) => !d.satellites_count), test: (d) => !d.satellites_count },
      ].filter((filter) => filter.key === "all" || filter.count > 0 || filter.key === this.activeFilter);
    },
    visibleDomains() {
      const filter = this.filters.find((f) => f.key === this.activeFilter) || this.filters[0];
      return this.scopedDomains.filter(filter.test);
    },
    // Router of all the selected domains if it is the same, undefined otherwise
    commonRouterId() {
      const ids = new Set(this.selected.map((d) => (d.router ? d.router.id : null)));
      return ids.size === 1 ? [...ids][0] : undefined;
    },
    entries() {
      return parseHosts(this.hostsText);
    },
    clientError() {
      return this.entries.length > MAX_HOSTS ? this.$t("satellites.domains.errors.max_hosts", { number: MAX_HOSTS }) : "";
    },
    hostErrors() {
      return Object.keys(this.violations)
        .map((key) => /^hosts\.(\d+)$/.exec(key))
        .filter((match) => match && this.sentEntries[Number(match[1])])
        .map((match) => {
          const index = Number(match[1]);
          return { index, ...this.sentEntries[index], message: messagesOf(this.violations[match[0]]) };
        })
        .sort((a, b) => a.index - b.index);
    },
    placementError() {
      return messagesOf(Object.keys(this.violations).filter((key) => key.startsWith("placement")).map((key) => messagesOf(this.violations[key])));
    },
  },
  watch: {
    activeFilter: "clearSelection",
    "$route.query": "clearSelection",
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      return Promise.all([satelliteService.getDomains(), satelliteService.getRouters(), satelliteService.getGroups(), satelliteService.getSatellites()])
        .then(([domains, routers, groups, satellites]) => {
          if (domains && domains.status === 403) {
            this.$router.replace({ name: "Dashboard" }).catch(() => {});
            return;
          }
          if (!isOk(domains)) {
            this.showError(domains);
            return;
          }
          this.applyDomains(domains.data || {});
          if (isOk(routers)) this.routers = [...(routers.data.routers || [])].sort((a, b) => a.name.localeCompare(b.name));
          if (isOk(groups)) this.groups = groups.data || [];
          if (isOk(satellites)) this.satellites = satellites.data || [];
          if (this.$route.query.add) this.openAdd();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fetchDomains() {
      return satelliteService.getDomains().then((res) => {
        if (isOk(res)) this.applyDomains(res.data || {});
      });
    },
    applyDomains(data) {
      this.domains = Array.isArray(data.domains) ? data.domains : [];
      this.count = Number.isInteger(data.count) ? data.count : this.domains.length;
      this.limit = Number.isInteger(data.limit) ? data.limit : 0;
      this.selected = [];
    },
    replaceDomains(updated) {
      const byId = Object.fromEntries(updated.map((domain) => [domain.id, domain]));
      this.domains = this.domains.map((domain) => byId[domain.id] || domain);
    },
    setFilter(key) {
      this.activeFilter = key;
    },
    clearScope() {
      this.$router.replace({ query: {} }).catch(() => {});
    },
    onSelectionChange(params) {
      this.selected = params.selectedRows;
    },
    clearSelection() {
      this.selected = [];
      if (this.$refs.table) this.$refs.table.unselectAllInternal();
    },

    // Inline and bulk changes
    updateOne(domain, changes) {
      if (changes.router_id !== undefined && (domain.router ? domain.router.id : null) === changes.router_id) return;
      this.savingIds.push(domain.id);
      satelliteService
        .updateDomain(domain.id, changes)
        .then((res) => {
          if (isOk(res)) {
            this.replaceDomains([res.data.domain]);
            this.$alert({ type: "success", title: this.$t("satellites.domains.saved", { host: domain.host }) });
          } else {
            this.showError(res);
          }
        })
        .finally(() => {
          this.savingIds = this.savingIds.filter((id) => id !== domain.id);
        });
    },
    bulkAssign(changes, domains = this.selected) {
      if (this.bulkSaving || !domains.length) return Promise.resolve(false);
      this.bulkSaving = true;
      this.violations = {};
      return satelliteService
        .updateDomains(
          domains.map((d) => d.id),
          changes
        )
        .then((res) => {
          if (isOk(res)) {
            this.replaceDomains(res.data.domains || []);
            this.clearSelection();
            this.$alert({ type: "success", title: this.pl("satellites.domains.saved_many", domains.length) });
            return true;
          }
          if (res && res.status === 422 && res.data && res.data.violations) this.violations = res.data.violations;
          else this.showError(res);
          return false;
        })
        .finally(() => {
          this.bulkSaving = false;
        });
    },
    openPlacement(domains) {
      this.placementTargets = domains;
      this.violations = {};
      const drafts = domains.map((d) => placementDraftOf(d.placement));
      this.placementDraft = drafts.every((draft) => isEqual(draft, drafts[0])) ? drafts[0] : { mode: "all", group_ids: [], satellite_ids: [] };
      this.$modal.show("satelliteDomainsPlacement");
    },
    savePlacement() {
      this.bulkAssign({ placement: placementBody(this.placementDraft) }, this.placementTargets).then((ok) => {
        if (ok) this.$modal.hide("satelliteDomainsPlacement");
      });
    },
    askDelete(domains) {
      this.deleteTargets = domains;
      this.$modal.show("satelliteDomainsDelete");
    },
    remove() {
      if (this.bulkSaving || !this.deleteTargets.length) return;
      this.bulkSaving = true;
      const count = this.deleteTargets.length;
      satelliteService
        .deleteDomains(this.deleteTargets.map((d) => d.id))
        .then((res) => {
          if (isOk(res)) {
            this.$modal.hide("satelliteDomainsDelete");
            this.$alert({ type: "success", title: this.pl("satellites.domains.deleted_many", count) });
            this.fetchDomains();
          } else {
            this.showError(res);
          }
        })
        .finally(() => {
          this.bulkSaving = false;
        });
    },

    // Add
    openAdd() {
      if (this.limitReached) return;
      this.$modal.show("satelliteDomainsAdd");
      this.$nextTick(() => setTimeout(() => this.$refs.hosts && this.$refs.hosts.focus(), 150));
    },
    resetAdd() {
      this.violations = {};
      this.addError = "";
      if (this.$route.query.add) this.$router.replace({ query: { ...this.$route.query, add: undefined } }).catch(() => {});
    },
    addViolation(key) {
      return messagesOf(this.violations[key]);
    },
    add() {
      if (this.adding || this.clientError || !this.entries.length) return;
      this.adding = true;
      this.violations = {};
      this.addError = "";
      const entries = this.entries;
      this.sentEntries = entries;
      satelliteService
        .addDomains({ hosts: entries.map((entry) => entry.host), router_id: this.addRouterId, placement: placementBody(this.addPlacement) })
        .then((res) => {
          if (isOk(res)) {
            const data = res.data || {};
            const added = Array.isArray(data.domains) ? data.domains.length : 0;
            const skipped = Array.isArray(data.skipped) ? data.skipped.length : 0;
            let title = this.pl("satellites.domains.added", added);
            if (!added) title = this.$t("satellites.domains.all_skipped", { skipped });
            else if (skipped) title = `${title}. ${this.$t("satellites.domains.skipped", { skipped })}`;
            this.$alert({ type: "success", title });
            this.hostsText = "";
            this.sentEntries = [];
            this.$modal.hide("satelliteDomainsAdd");
            this.fetchDomains();
          } else if (res && res.status === 422 && res.data && res.data.violations) {
            this.violations = res.data.violations;
            this.addError = messagesOf(this.violations.hosts);
          } else if (res && res.status === 409) {
            const limit = res.data && Number.isInteger(res.data.limit) ? res.data.limit : this.limit;
            this.addError = this.$t("satellites.domains.limit_exceeded", { limit, free: Math.max(limit - this.count, 0) });
          } else {
            this.addError = (res && res.message) || this.$t("server_error");
          }
        })
        .finally(() => {
          this.adding = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.domains-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
}

.domains-search {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 1 320px;
  min-width: 220px;
  padding: 0 12px;
  border-radius: 10px;
  color: var(--sat-muted);
  background: var(--sat-input);

  input {
    flex: 1;
    min-width: 0;
    height: 42px;
    padding: 0;
    border: 0 !important;
    background: transparent !important;
  }
}

.domains-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.domains-filter {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
  border: 1px solid var(--sat-border);
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  color: var(--sat-text-2);
  background: transparent;
  cursor: pointer;

  &:hover {
    border-color: var(--sat-accent);
  }

  &._active {
    color: var(--sat-accent);
    border-color: var(--sat-accent);
    background: var(--sat-accent-soft);
  }

  &:focus {
    outline: none;
  }
}

.domains-filter__count {
  font-size: 12px;
  color: var(--sat-muted);
}

.domains-counter {
  margin-left: auto;
  font-size: 13px;
  color: var(--sat-muted);

  &._full {
    color: var(--sat-warn);
    font-weight: 600;
  }
}

.domains-active-filter {
  margin-top: 12px;
}

.domains-active-filter__clear {
  display: inline-flex;
  padding: 0;
  margin-left: 2px;
  border: 0;
  color: inherit;
  background: transparent;
  cursor: pointer;
}

.domains-host {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 180px;
}

.domains-host__name {
  font-size: 15px;
  font-weight: 600;
  color: var(--sat-text);
  word-break: break-all;
  white-space: normal;

  &:hover {
    color: var(--sat-accent);
  }
}

.domains-host__ssl {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--sat-muted);

  &._ok {
    color: var(--sat-ok);
  }
}

.domains-cell-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 280px;
  padding: 6px 10px;
  margin-left: -10px;
  border: 1px solid transparent;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  color: var(--sat-text);
  background: transparent;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover:not(:disabled) {
    border-color: var(--sat-border);
    background: var(--sat-hover);
  }

  &._empty {
    color: var(--sat-warn);
    font-weight: 500;
  }

  &._plain {
    font-weight: 500;
  }

  &:disabled {
    opacity: 0.5;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px var(--sat-accent);
  }
}

.domains-cell-btn__chevron {
  color: var(--sat-muted);
}

.domains-sats {
  font-size: 12px;
  color: var(--sat-muted);
  white-space: nowrap;

  &._none {
    color: var(--sat-warn);
  }
}

.domains-controls {
  display: flex;
  justify-content: flex-end;
  gap: 4px;
}

.domains-danger-btn {
  color: var(--sat-danger) !important;
  border-color: var(--sat-danger) !important;
  background: transparent !important;

  &:hover:not(:disabled) {
    color: #fff !important;
    background: var(--sat-danger) !important;
  }
}

.domains-empty-help {
  width: 100%;
  max-width: 640px;
  margin-top: 18px;
  text-align: left;
}

.domains-modal {
  position: relative;
  padding: 16px 24px 24px;

  @media (max-width: 575px) {
    padding: 12px 16px 20px;
  }
}

.domains-modal__subtitle {
  margin: 4px 0 0;
  font-size: 14px;
  text-align: center;
  color: var(--sat-muted);
  word-break: break-all;
}

.domains-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
}

.domains-textarea {
  width: 100%;
  resize: vertical;
}

.domains-host-errors {
  margin: 6px 0 0;
  padding-left: 0;
  font-size: 12px;
  color: var(--sat-danger);
  list-style: none;
  word-break: break-all;
}

.domains-router-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.domains-router-option {
  padding: 8px 14px;
  border: 1px solid var(--sat-border);
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: var(--sat-text-2);
  background: var(--sat-card);
  cursor: pointer;

  &:hover {
    border-color: var(--sat-accent);
  }

  &._active {
    color: var(--sat-accent);
    border-color: var(--sat-accent);
    background: var(--sat-accent-soft);
  }

  &:focus {
    outline: none;
  }
}
</style>
